import React, { ComponentType, FC, useState, useEffect } from 'react';
import {
  ItemTypeToComponent,
  PaginationComponentProps,
  UltimatePaginationProps,
  createUltimatePagination,
} from 'react-ultimate-pagination';
import { i18n } from '../../../i18n/translate';
import { formatClassName } from '../../../utils';
import { Button, ButtonSize } from '../Button';
import { IconButton } from '../IconButton';
import { List, ListItem } from '../List';
import { PaginationComponentButtonProps, PaginationComponentWrapperProps } from './types';

const PaginationComponentButton: FC<PaginationComponentButtonProps> = props => {
  const { isDisabled, label, onClick, value } = props;
  return (
    <ListItem>
      {/* since we are staying on the same page, ok to use button element */}
      <Button
        isActive={props.isActive}
        isDisabled={isDisabled}
        onClick={onClick}
        aria-label={label || i18n.t('Go to page {{value}}', { value })}
        {...(props.isActive ? { 'aria-current': 'page' } : {})}
        size={ButtonSize.COMPACT}
      >
        {value}
      </Button>
    </ListItem>
  );
};

const PaginationComponentPage: FC<PaginationComponentProps> = props =>
  PaginationComponentButton(props as PaginationComponentButtonProps);

const PaginationComponentEllipsis: FC<PaginationComponentProps> = props =>
  PaginationComponentButton({
    ...props,
    isDisabled: true,
    onClick: () => {},
    value: '…',
    label: i18n.t('More'),
  } as PaginationComponentButtonProps);

const PaginationComponentFirstPageLink: FC<PaginationComponentProps> = props =>
  PaginationComponentButton({
    ...props,
    isDisabled: props.isActive,
    isArrowButton: true,
    value: '«',
    label: i18n.t('First'),
  } as PaginationComponentButtonProps);

const PaginationComponentPreviousPageLink: FC<PaginationComponentProps> = props => {
  return (
    <ListItem>
      <IconButton
        ariaLabel={i18n.t('Previous')}
        icon={{ prefix: 'fas', iconName: 'arrow-left' }}
        variant="ghost"
        size="compact"
        isDisabled={props.isActive}
        aria-disabled={props.isActive ? true : undefined}
        onClick={props.onClick}
      />
    </ListItem>
  );
};

const PaginationComponentNextPageLink: FC<PaginationComponentProps> = props => {
  return (
    <ListItem>
      <IconButton
        ariaLabel={i18n.t('Next')}
        icon={{ prefix: 'fas', iconName: 'arrow-right' }}
        variant="ghost"
        size="compact"
        isDisabled={props.isActive}
        aria-disabled={props.isActive ? true : undefined}
        onClick={props.onClick}
      />
    </ListItem>
  );
};

const PaginationComponentLastPageLink: FC<PaginationComponentProps> = props =>
  PaginationComponentButton({
    ...props,
    isDisabled: props.isActive,
    isArrowButton: true,
    value: '»',
    label: i18n.t('Last'),
  } as PaginationComponentButtonProps);

const PaginationComponentWrapper: FC<PaginationComponentWrapperProps> = props => {
  if (props.meta.pageCount <= 1) return null;
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    setAnnouncement(
      i18n.t('Loaded page {{page}} of {{total}}', { page: props.meta.currentPage, total: props.meta.pageCount })
    );
  }, [props.meta.currentPage, props.meta.pageCount]);

  const className = formatClassName([
    props.coreTable ? 'core-pagination' : 'pagination',
    { 'pagination-branded': props.isBranded },
  ]);

  return (
    <nav aria-label={i18n.t('Pagination')}>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <List
        className={className}
        listStyleType="none"
        display={'flex'}
        maxW="100%"
        overflowX="auto"
        overflowY="hidden"
        sx={{
          '& > *:not(style) ~ *:not(style)': {
            margin: 0,
          },
        }}
        m={0}
        p={0}
        alignItems={'center'}
        gap={1}
      >
        {props.children}
      </List>
    </nav>
  );
};

const itemTypeToComponent: ItemTypeToComponent = {
  PAGE: PaginationComponentPage,
  ELLIPSIS: PaginationComponentEllipsis,
  FIRST_PAGE_LINK: PaginationComponentFirstPageLink,
  PREVIOUS_PAGE_LINK: PaginationComponentPreviousPageLink,
  NEXT_PAGE_LINK: PaginationComponentNextPageLink,
  LAST_PAGE_LINK: PaginationComponentLastPageLink,
};

const UltimatePagination = createUltimatePagination({
  itemTypeToComponent,
  WrapperComponent: PaginationComponentWrapper,
});

export const PaginationComponent = UltimatePagination as ComponentType<
  UltimatePaginationProps & PaginationComponentWrapperProps
>;
